import Link from 'next/link';
import styled from 'styled-components';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import snakeCase from 'lodash/snakeCase';

import { IConstructorSortOption } from 'shared/types/ConstructorSortOption';
import { SearchFiltersHoverAndSelectedStyle } from 'search/styles/SearchStyles';
import { getCurrentSort } from 'shared/utils/gridEventTrackingUtils';
import { getUpcomingReleasesDateRange } from 'search/utils/getUpcomingReleasesDateRange';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { getConstructorSortStrings } from 'search/enums/translations';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';

interface ISearchSort {
  sortOptions: Array<IConstructorSortOption>;
}

const SearchSortDesktop = ({ sortOptions }: ISearchSort) => {
  const router = useRouter();
  const { query, asPath, pathname } = router;
  const eventTrackingEventContext = useEventContext();
  const isConsumerSearchEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  useEffect(() => {
    const hasSelectedSortOption = sortOptions.some((option) => option.status === 'selected');
    if (!hasSelectedSortOption) {
      void router.push({
        pathname,
        query: { ...query, sortBy: 'relevance', sortOrder: 'ascending' },
      });
    }
  }, []);

  const getQuery = (sortOption) => {
    const routerQuery = { ...query };

    // Upcoming Releases sort has an implicit release_date filter of tmr - 1 year from tmr
    if (sortOption.display_name === 'Upcoming Releases') {
      const { min, max } = getUpcomingReleasesDateRange(asPath);
      return {
        ...(routerQuery.query && { query: routerQuery.query }),
        ...(routerQuery.timelineYear && { timelineYear: routerQuery.timelineYear }),
        sortBy: sortOption.sort_by,
        sortOrder: sortOption.sort_order,
        release_date: `${min}-${max}`,
      };
    }

    // If recently_released facet is currently selected as a sort option, but
    // we are changing to a native sort option (Popular or Price), we remove the recently_released facet
    if (routerQuery.recently_released && sortOption.sort_by) {
      delete routerQuery.recently_released;
    }
    // We need to sort by date_added descending for New In Apparel,
    // and release_date descending for Recently Released Sneakers
    if (['apparel', 'sneakers'].includes(sortOption.value)) {
      return {
        ...routerQuery,
        recently_released: sortOption.value,
        sortBy: sortOption.value === 'apparel' ? 'date_added' : 'release_date',
        sortOrder: 'descending',
      };
    }

    // When we change sort to something other than Upcoming Releases, we remove the release_date filter
    if (sortOption.display_name !== 'Upcoming Releases') {
      delete routerQuery.release_date;
    }

    return {
      ...routerQuery,
      sortBy: sortOption.sort_by,
      sortOrder: sortOption.sort_order,
    };
  };

  const handleOnClick = (sortOption) => {
    sendTrackingEvent('FILTER_SORT_TAP', {
      service_name: isConsumerSearchEnabled ? 'consumer_search' : 'constructor',
      event_context: eventTrackingEventContext,
      sort_selection: getCurrentSort(
        sortOption?.sort_by,
        sortOption?.sort_order,
        sortOption?.value,
      ),
    });
  };

  return (
    <SearchSortDesktop.Wrapper>
      {sortOptions.map((sortOption) => {
        // todo: looks like we have duplicate sortoptions here on staging
        return (
          <Link
            href={{
              pathname,
              query: getQuery(sortOption),
            }}
            key={sortOption.display_name}
            passHref
            prefetch={false}
            scroll
            shallow
            legacyBehavior
          >
            <SearchSortDesktop.SortByOption
              $isSelected={sortOption.status === 'selected'}
              onClick={() => handleOnClick(sortOption)}
              data-qa={`${snakeCase(sortOption.display_name)}_sort_option`}
            >
              {getConstructorSortStrings(sortOption.display_name)}
            </SearchSortDesktop.SortByOption>
          </Link>
        );
      })}
    </SearchSortDesktop.Wrapper>
  );
};

SearchSortDesktop.Wrapper = styled.div`
  padding: 0 0 32px;
`;

SearchSortDesktop.SortByOption = styled.a<{ $isSelected?: boolean }>`
  cursor: pointer;
  display: block;
  margin: 0;
  padding: 0 0 4px;
  text-align: right;
  width: 100%;
  ${({ $isSelected }) => $isSelected && SearchFiltersHoverAndSelectedStyle};

  &:hover {
    ${SearchFiltersHoverAndSelectedStyle};
  }
`;

export default SearchSortDesktop;
